import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => read("lj_cart"));
  const [wishlist, setWishlist] = useState(() => read("lj_wishlist"));
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [trackOpen, setTrackOpen] = useState(false);
  const [stylistOpen, setStylistOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("lj_customer_token")) {
      api.get("/auth/customer/me")
        .then((r) => setCustomer(r.data))
        .catch(() => localStorage.removeItem("lj_customer_token"));
    }
  }, []);

  useEffect(() => localStorage.setItem("lj_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("lj_wishlist", JSON.stringify(wishlist)), [wishlist]);

  const addToCart = (product, size, qty = 1) => {
    const key = `${product.id}__${size}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + qty, 10) } : i));
      return [...prev, { key, product_id: product.id, name: product.name, price: product.price, image: product.images?.[0] || "", size, qty }];
    });
    toast.success(`${product.name} (${size}) added to your basket`);
  };

  const updateQty = (key, delta) =>
    setCart((prev) =>
      prev.map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i)).filter((i) => i.qty > 0)
    );

  const removeItem = (key) => setCart((prev) => prev.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const toggleWishlist = (productId) => {
    setWishlist((prev) => {
      const has = prev.includes(productId);
      toast.success(has ? "Removed from wishlist" : "Saved to wishlist");
      return has ? prev.filter((id) => id !== productId) : [...prev, productId];
    });
  };

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);

  const value = {
    cart, cartCount, subtotal, addToCart, updateQty, removeItem, clearCart,
    wishlist, toggleWishlist,
    cartOpen, setCartOpen, checkoutOpen, setCheckoutOpen,
    selectedProduct, setSelectedProduct,
    trackOpen, setTrackOpen, stylistOpen, setStylistOpen,
    customer, setCustomer, loginOpen, setLoginOpen, ordersOpen, setOrdersOpen,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
